import { useStripe } from "@stripe/react-stripe-js";
import { PaymentIntent } from "@stripe/stripe-js/types/api/payment-intents";
import { useEnhancedLazyQuery } from "@uplift-ltd/apollo";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  OrderStatusQuery,
  OrderStatusVariables,
  STRIPE_ORDER_STATUS,
  usePostCheckoutWithStripe,
} from "components/Cart/queries";
import {
  getStripeClientSecret,
  StripeStatus,
  triggerPurchaseConversionTrackers,
} from "components/Cart/utils";
import Alert, { AlertTheme } from "components/common/Alert";
import { FullPageLoading } from "components/common/Loading";
import useRouterNavigation from "hooks/useRouterNavigation";
import { StripeElementsForResult } from "./StripeElements";

interface Props {
  status: StripeStatus;
}

const Message = ({ status }: Props) => {
  const [message, setMessage] = useState<string | null>(null);
  const [theme, setTheme] = useState<AlertTheme>("danger");

  useEffect(() => {
    switch (status) {
      case "succeeded":
        setMessage("Payment succeeded!");
        setTheme("success");
        break;
      case "processing":
        setMessage("Your payment is processing.");
        break;
      case "requires_payment_method":
        setMessage("Your payment was not successful, please try again.");
        break;
      default:
        setMessage("Something went wrong.");
        break;
    }
  }, [status]);

  if (!message) {
    return null;
  }

  return <Alert theme={theme}>{message}</Alert>;
};

interface CheckoutStatusProps {
  onSuccessRedirectUrl?: string | (() => string);
  onSuccessHandler?: () => void;
}

interface CheckoutStatusWithLoadingHandlerProps extends CheckoutStatusProps {
  onLoadingHandler?: (isLoading: boolean) => void;
}

const StripeCheckoutResult = ({
  onSuccessRedirectUrl,
  onSuccessHandler,
  onLoadingHandler,
}: CheckoutStatusWithLoadingHandlerProps) => {
  const { replace } = useRouterNavigation();
  const stripe = useStripe();
  const postPurchaseQueries = usePostCheckoutWithStripe();
  const [clientSecret, setClientSecret] = useState<string | null>();
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<StripeStatus>(null);
  const [processingStatus, setProcessingStatus] = useState<
    "pending" | "loading" | "verifying" | "complete"
  >("pending");
  const [successStatusReceived, setSuccessStatusReceived] = useState(false);
  const timeout = useRef<NodeJS.Timeout>();
  const isLoading = processingStatus !== "complete";
  const isIntentLoading = processingStatus === "pending" || processingStatus === "loading";

  const [getOrderStatus, { data, loading: loadingOrderStatus, refetch }] = useEnhancedLazyQuery<
    OrderStatusQuery,
    OrderStatusVariables
  >(STRIPE_ORDER_STATUS);

  // Load PaymentIntent if a purchase has been attempted
  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecretFromQueryString = getStripeClientSecret();
    setClientSecret(clientSecretFromQueryString);

    if (!clientSecretFromQueryString) {
      setPaymentStatus(false);
      setProcessingStatus("complete");
      return;
    }

    const getResult = async () => {
      const { paymentIntent: stripePaymentIntent } = await stripe.retrievePaymentIntent(
        clientSecretFromQueryString
      );
      if (stripePaymentIntent) {
        setPaymentIntent(stripePaymentIntent);
      }
      setPaymentStatus(stripePaymentIntent?.status || false);
    };

    if (processingStatus === "pending") {
      setProcessingStatus("loading");
      getResult();
    }
  }, [processingStatus, stripe]);

  // If a payment has succeeded, empty the cart and check if order is complete
  useEffect(() => {
    if (paymentStatus) {
      if (paymentStatus === "succeeded") {
        setProcessingStatus("verifying");

        if (!successStatusReceived) {
          setSuccessStatusReceived(true);
          getOrderStatus({
            variables: { paymentIntentClientSecret: clientSecret || "" },
          });
        }
      } else {
        setProcessingStatus("complete");
      }
    }
  }, [clientSecret, getOrderStatus, paymentStatus, successStatusReceived]);

  // If a payment has succeeded and the order is incomplete, we'll wait for it
  useEffect(() => {
    if (
      successStatusReceived &&
      !loadingOrderStatus &&
      refetch &&
      data?.orderStatus?.isCompleted === false
    ) {
      timeout.current = setInterval(() => refetch(), 5000);
    }
    return () => {
      if (timeout.current) {
        clearInterval(timeout.current);
      }
    };
  }, [data?.orderStatus?.isCompleted, loadingOrderStatus, refetch, successStatusReceived]);

  // If the order is complete, run the success handler and do the redirect if necessary
  useEffect(() => {
    if (data?.orderStatus?.isCompleted === true) {
      if (timeout.current) {
        clearInterval(timeout.current);
      }

      postPurchaseQueries();

      if (onSuccessHandler) {
        onSuccessHandler();
      }

      triggerPurchaseConversionTrackers(
        paymentIntent?.amount ? paymentIntent?.amount / 100 : 0,
        paymentIntent?.currency || "USD",
        paymentIntent?.id || "",
        data.orderStatus.productIds
      );

      // Either redirect...
      if (onSuccessRedirectUrl) {
        replace(
          typeof onSuccessRedirectUrl === "function" ? onSuccessRedirectUrl() : onSuccessRedirectUrl
        );
      }
      // ...or set as complete
      else {
        setProcessingStatus("complete");
      }
    }
  }, [
    data?.orderStatus,
    onSuccessHandler,
    onSuccessRedirectUrl,
    paymentIntent?.amount,
    paymentIntent?.currency,
    paymentIntent?.id,
    postPurchaseQueries,
    replace,
  ]);

  // Update the parent on the loading status if a handler has been provided
  useEffect(() => {
    if (onLoadingHandler) {
      onLoadingHandler(isLoading);
    }
  }, [isLoading, onLoadingHandler]);

  if (isIntentLoading || !paymentStatus) {
    return null;
  }

  return <Message status={paymentStatus} />;
};

interface ChildrenProps {
  children?: React.ReactNode;
  loading?: boolean;
}

interface CheckoutStatusPropsWithChildren extends CheckoutStatusProps, ChildrenProps {}

export const StripeCheckoutStatus = ({
  onSuccessHandler,
  onSuccessRedirectUrl,
  children,
  loading,
}: CheckoutStatusPropsWithChildren) => {
  const [purchaseLoadingOrRedirecting, setPurchaseLoadingOrRedirecting] = useState(true);
  const isLoading = loading || purchaseLoadingOrRedirecting;

  const onLoadingHandler = useCallback((isPurchaseLoading: boolean) => {
    setPurchaseLoadingOrRedirecting(isPurchaseLoading);
  }, []);

  return (
    <>
      <StripeElementsForResult onLoadingHandler={onLoadingHandler}>
        <StripeCheckoutResult
          onLoadingHandler={onLoadingHandler}
          onSuccessHandler={onSuccessHandler}
          onSuccessRedirectUrl={onSuccessRedirectUrl}
        />
      </StripeElementsForResult>
      {isLoading ? <FullPageLoading /> : <>{children}</>}
    </>
  );
};
